import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Field } from '@/design-system';
import { acceptFor, checkUpload, type FilePurpose } from '@/shared/api';

import { fileHint, uploadProblemMessage } from './messages';

export interface FileFieldProps {
  label: string;
  purpose: FilePurpose;
  onChange: (file: File | null) => void;
  optional?: boolean;
  /** Form-level error (e.g. required). The policy check shows its own message. */
  error?: string | undefined;
  /** Extra help before the policy hint. */
  help?: string;
}

/**
 * A form file input with the file policy built in: allowed types in `accept`, the chosen file checked at once
 * (type and size per purpose) and refused with a plain message — the dialog never uploads a file the API would refuse.
 */
export function FileField({ label, purpose, onChange, optional, error, help }: FileFieldProps) {
  const { t } = useTranslation();
  const [problem, setProblem] = useState<string | null>(null);
  const hint = fileHint(t, purpose);
  return (
    <Field label={label} optional={optional} error={problem ?? error} help={help ? `${help} ${hint}` : hint}>
      <input
        type="file"
        accept={acceptFor(purpose)}
        className="block w-full rounded-sm border border-border bg-surface text-body text-ink file:me-3 file:border-0 file:border-e file:border-border file:bg-surface-muted file:px-3 file:py-1.5 file:text-label"
        onChange={(e) => {
          const file = e.target.files?.[0] ?? null;
          const bad = file ? checkUpload(file, purpose) : null;
          if (file && bad) {
            setProblem(uploadProblemMessage(t, bad, file.name));
            e.target.value = '';
            onChange(null);
            return;
          }
          setProblem(null);
          onChange(file);
        }}
      />
    </Field>
  );
}
